import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import './AuthLogin.css';
import { LoginNavbar } from '../../components/layout/Navbar';
import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import {
  login,
  extractLoginResult,
  setRememberMeCookie,
  useAuthToken,
} from '../../auth';
import { shouldPersistAccessToken } from '../../auth/tokenPersist';
import '../../components/layout/LayoutComponents.css';

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const Footer = ({ className, footerText }) => (
  <footer className={className || 'footer'}>
    {footerText}
  </footer>
);

const Login = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAccessToken } = useAuthToken();
  const { loading, error } = useSelector((state) => state.auth);
  const selectedLang = useSelector((state) => state.language?.selected || 'en');
  const [showPassword, setShowPassword] = useState(false);

  useLayoutEffect(() => {
    if (selectedLang && i18n.language !== selectedLang) {
      void i18n.changeLanguage(selectedLang);
    }
  }, [selectedLang, i18n]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  // Lock scroll on desktop only; allow scroll on mobile for keyboard/small screens
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 769px)');
    const applyScrollLock = () => {
      if (mediaQuery.matches) {
        document.body.style.overflow = 'hidden';
        document.body.style.height = '100vh';
      } else {
        document.body.style.overflow = '';
        document.body.style.height = '';
      }
    };

    applyScrollLock();
    mediaQuery.addEventListener('change', applyScrollLock);

    return () => {
      mediaQuery.removeEventListener('change', applyScrollLock);
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  const onSubmit = async (values) => {
    try {
      dispatch(loginStart());

      const rememberMe = Boolean(values.rememberMe);
      const res = await login({
        email: values.email,
        password: values.password,
        rememberMe,
      });

      const { accessToken, user } = extractLoginResult(res);
      if (!accessToken) throw new Error(t('login.errorNoToken'));
      if (!user) throw new Error(t('login.errorNoUser'));

      const isEmployee = user?.roles?.some((r) => r.roleName === 'EMPLOYEE');
      if (!isEmployee) throw new Error(t('login.errorNotEmployee'));

      // Cookie first so other readers see the new session type, then JWT.
      setRememberMeCookie(rememberMe);
      setAccessToken(accessToken, {
        persistSession: shouldPersistAccessToken(rememberMe),
      });

      // Drop foreign / legacy keys (admin leftover, old logins). Only ezts_auth is used here.
      ['auth-storage', 'token', 'auth'].forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore
        }
      });

      dispatch(
        loginSuccess({
          user,
          rememberMe,
        })
      );

      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Login error:', err?.response?.data || err?.message);

      const status = err?.response?.status;
      let message;

      if (status === 401) {
        message = t('login.invalidCredentials');
      } else {
        message = err?.response?.data?.message || err?.message || t('login.loginFailedGeneric');
      }

      dispatch(loginFailure(message));
    }
  };

  const validate = (values) => {
    const errors = {};

    if (!values.email) {
      errors.email = t('login.emailRequired');
    } else if (
      String(values.email).includes('@') &&
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(values.email)
    ) {
      errors.email = t('login.emailInvalid');
    }

    if (!values.password) {
      errors.password = t('login.passwordRequired');
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,16}$/.test(
        values.password
      )
    ) {
      errors.password = t('login.passwordRules');
    }

    return errors;
  };

  return (
    <div className="auth-login-page">
      <LoginNavbar />
      <div className="auth-login-container">
        <div className="auth-login-card">
          <h1 className="auth-login-title" key={`login-title-${i18n.language}`}>
            {t('login.title')}
          </h1>
          <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{ rememberMe: false }}
            render={({ handleSubmit, submitting }) => (
              <form onSubmit={handleSubmit}>
                <div className="auth-form-group auth-form-row">
                  <label className="auth-label" htmlFor="login-email">
                    {t('login.emailLabel')}
                  </label>
                  <Field name="email">
                    {({ input, meta }) => (
                      <>
                        <input
                          {...input}
                          id="login-email"
                          type="text"
                          placeholder={t('login.emailPlaceholder')}
                          className="auth-input"
                          required
                        />
                        {meta.touched && meta.error && (
                          <div className="auth-error-message">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>

                <div className="auth-form-group auth-form-row">
                  <label className="auth-label" htmlFor="login-password">
                    {t('login.passwordLabel')}
                  </label>
                  <Field name="password">
                    {({ input, meta }) => (
                      <>
                        <div className="auth-password-field">
                          <input
                            {...input}
                            id="login-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('login.passwordPlaceholder')}
                            className="auth-input auth-input-password"
                            required
                          />
                          <button
                            type="button"
                            className="auth-password-toggle"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                            title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                          >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                          </button>
                        </div>
                        {meta.touched && meta.error && (
                          <div className="auth-error-message">{meta.error}</div>
                        )}
                      </>
                    )}
                  </Field>
                </div>

                <div className="auth-checkbox-row">
                  <div className="auth-checkbox-group">
                    <Field name="rememberMe" type="checkbox">
                      {({ input }) => (
                        <input {...input} id="login-remember-me" type="checkbox" />
                      )}
                    </Field>
                    <label className="auth-label" htmlFor="login-remember-me">
                      {t('login.rememberMe')}
                    </label>
                  </div>
                  <Link to="/forgot-password" className="auth-forgot-password-inline">
                    {t('login.forgotPassword')}
                  </Link>
                </div>

                {error && <div className="auth-error-message">{error}</div>}

                <button type="submit" disabled={loading || submitting} className="auth-submit-button">
                  {loading ? t('login.submitting') : t('login.submit')}
                </button>

                <div className="auth-legal-links">
                  <Link to="/terms">{t('login.terms')}</Link>
                  <span style={{ margin: '0 8px' }}></span>
                  <Link to="/privacy">{t('login.privacy')}</Link>
                </div>
              </form>
            )}
          />
        </div>
      </div>
      <Footer className="login-footer" footerText={t('login.footer')} />
    </div>
  );
};

export default Login;
